import type {
  IndeedIntegrationSettings,
  UpdateIndeedFeedSettings,
} from 'api-clients/monolith';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useConfirmation } from '@/components/base/confirmation';
import { useToaster } from '@/components/base/toaster';
import { ExternalLinkIcon } from '@/components/icons/external-link';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography';
import {
  useIndeedSettingsWriting,
  useUnlinkIndeed,
  useUpdateIndeedSettings,
} from '@/hooks/use-indeed-settings';
import globalMessages from '@/i18n/global-messages';
import { formatShortDate } from '@/utils/date-util';

import {
  getIndeedSettingsVisibility,
  IndeedSettingsFields,
  type IndeedSettingsValues,
} from './indeed-settings-fields';

export function ConfigureMode({
  settings,
  isOrganic,
  onClose,
}: {
  settings: IndeedIntegrationSettings;
  isOrganic: boolean;
  onClose: () => void;
}) {
  const intl = useIntl();
  const { confirm } = useConfirmation();
  const { notifySuccess } = useToaster();
  const update = useUpdateIndeedSettings();
  const unlink = useUnlinkIndeed();
  const writing = useIndeedSettingsWriting();
  const { showTitleSource, showToggles } =
    getIndeedSettingsVisibility(settings);
  const [value, setValue] = useState<IndeedSettingsValues>({
    indeed_xml_feed_enabled:
      (isOrganic && showToggles) || settings.indeed_xml_feed_enabled,
    indeed_apply_enabled: settings.indeed_apply_enabled,
    sourcing_title_source: settings.sourcing_title_source,
  });

  function save() {
    if (writing) return;
    const changed: UpdateIndeedFeedSettings = {};
    if (
      showToggles &&
      value.indeed_xml_feed_enabled !== settings.indeed_xml_feed_enabled
    )
      changed.indeed_xml_feed_enabled = value.indeed_xml_feed_enabled;
    if (
      showToggles &&
      value.indeed_apply_enabled !== settings.indeed_apply_enabled
    )
      changed.indeed_apply_enabled = value.indeed_apply_enabled;
    if (
      showTitleSource &&
      value.sourcing_title_source !== settings.sourcing_title_source
    )
      changed.sourcing_title_source = value.sourcing_title_source;
    update.mutate(changed, {
      onSuccess: () => {
        notifySuccess({
          message:
            changed.indeed_xml_feed_enabled === true
              ? intl.formatMessage({
                  defaultMessage: 'Indeed Organic Feed turned on.',
                  description:
                    'Confirms that the free Indeed job feed has been enabled',
                })
              : intl.formatMessage({
                  defaultMessage:
                    'Indeed settings saved. Changes reach Indeed at the next feed sync.',
                  description:
                    'Confirms Indeed settings are saved and explains when Indeed receives them',
                }),
        });
        onClose();
      },
    });
  }

  async function confirmUnlink() {
    if (writing) return;
    const confirmed = await confirm({
      title: intl.formatMessage({
        defaultMessage: 'Unlink Indeed account?',
        description:
          'Confirm disconnecting the employer Indeed account from Fountain',
      }),
      description: intl.formatMessage({
        defaultMessage:
          "You won't be able to sponsor jobs on Indeed or see Indeed performance analytics, and sponsored Indeed campaigns stop reporting to Fountain. Your feed settings stay saved if you connect again.",
        description:
          'Explains the consequences of unlinking Indeed and that feed settings are retained',
      }),
      cancelLabel: intl.formatMessage(globalMessages.cancel),
      confirmLabel: intl.formatMessage({
        defaultMessage: 'Unlink',
        description: 'Disconnect the employer Indeed account from Fountain',
      }),
      confirmVariant: 'destructive',
    });
    if (confirmed) unlink.mutate(undefined, { onSuccess: onClose });
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle>
          <FormattedMessage
            defaultMessage="Configure Indeed"
            description="Manage the connected Indeed account and its feed settings"
          />
        </SheetTitle>
      </SheetHeader>
      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={event => {
          event.preventDefault();
          save();
        }}
      >
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <div className="flex flex-col gap-1">
            <Typography as="h2" variant="h6">
              <FormattedMessage
                defaultMessage="Connected account"
                description="The Indeed employer account currently linked to Fountain"
              />
            </Typography>
            <Typography variant="subtitle1">
              <FormattedMessage
                defaultMessage="To connect to a different account, select Unlink and log in with your preferred Indeed credentials."
                description="Unlink the current Indeed account before connecting another"
              />
            </Typography>
          </div>
          <div className="flex flex-col gap-2">
            <Input
              id="indeed-connected-email"
              type="email"
              readOnly
              value={settings.indeed_authorization_email ?? ''}
              label={intl.formatMessage({
                defaultMessage: 'Indeed account email',
                description:
                  'Email address used to sign in to the employer Indeed account',
              })}
            />
            {settings.connected_by && (
              <Typography variant="small1" className="text-fx-neutral-600">
                <FormattedMessage
                  defaultMessage="Connected by {name}"
                  description="The user who connected the Indeed account"
                  values={{ name: settings.connected_by }}
                />
                {settings.connected_at && (
                  <> · {formatShortDate(settings.connected_at)}</>
                )}
              </Typography>
            )}
          </div>
          <div className="flex flex-col items-start gap-2">
            <a
              className="text-fx-brand-600 inline-flex min-h-6 items-center gap-1 underline"
              href="https://secure.indeed.com/settings"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage
                defaultMessage="Manage Account Settings on Indeed"
                description="Open the employer account settings on Indeed's website"
              />
              <ExternalLinkIcon className="text-fx-neutral-600" />
            </a>
            <a
              className="text-fx-brand-600 inline-flex min-h-6 items-center gap-1 underline"
              href="https://analytics.indeed.com/analytics/performance/ads"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage
                defaultMessage="View Performance Analytics on Indeed"
                description="Open job performance analytics on Indeed's website"
              />
              <ExternalLinkIcon className="text-fx-neutral-600" />
            </a>
            <Button
              type="button"
              variant="ghost"
              className="text-fx-rose"
              disabled={writing}
              onClick={() => {
                void confirmUnlink();
              }}
            >
              <FormattedMessage
                defaultMessage="Unlink"
                description="Disconnect the employer Indeed account from Fountain"
              />
            </Button>
          </div>
          {(update.isError || unlink.isError) && (
            <Typography
              role="alert"
              variant="subtitle1"
              className="text-fx-rose"
            >
              <FormattedMessage
                defaultMessage="We couldn't save your changes. Check your connection and try again."
                description="An Indeed settings or unlink request failed; the user can retry without losing edits"
              />
            </Typography>
          )}
          <IndeedSettingsFields
            settings={settings}
            value={value}
            onChange={setValue}
            organicPreset={
              isOrganic &&
              !settings.indeed_xml_feed_enabled &&
              value.indeed_xml_feed_enabled
            }
          />
          <Separator />
          <Typography variant="subtitle1">
            <FormattedMessage
              defaultMessage="Need help? <link>Contact Indeed Support</link> for issues such as account linking support and job posting visibility troubleshooting."
              description="Indeed support helps with account linking and job visibility"
              values={{
                link: chunks => (
                  <a
                    className="text-fx-brand-600 underline"
                    href="https://indeed.my.site.com/employerSupport1/s/article/207325006"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          </Typography>
        </div>
        <SheetFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            <FormattedMessage {...globalMessages.cancel} />
          </Button>
          <Button type="submit" disabled={writing}>
            <FormattedMessage {...globalMessages.save} />
          </Button>
        </SheetFooter>
      </form>
    </>
  );
}
